<template>
  <HeadTitle
    title="Compañias"
    description="En esta sección podrás ver las compañias de la plataforma"
  />
  <Toolset
    ref="toolset"
    :searcher="searcher"
    add-button-icon="pi pi-plus"
    add-button-label="Añadir compañias"
    :to-add="toAdd"
    class="mt-3"
  />
  <br />
  <DataTable
    class="tabla mb-5"
    :value="companies.data"
    paginator
    :rows="pagination.limit"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    tableStyle="min-width: 50rem"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    :totalRecords="companies.totalItems"
    :currentPageReportTemplate="currentPageReportTemplate"
    scrollable
    scrollHeight="600px"
    :loading="loading"
  >
    <template #empty> No se encontraron compañias. </template>
    <Column field="code" header="Código" sortable></Column>
    <Column field="name" header="name"></Column>
    <Column field="client" header="Cliente">
      <template #body="{ data, field }">
        {{ data[field].code }} - {{ data[field].name }}
      </template>
    </Column>
    <Column field="isInactive" sortable header="Estado">
      <template #body="{ data, field }">
        <Chip class="bg-pink-200 pt-1 pb-1" v-if="data[field]"> inactivo </Chip>
        <Chip class="bg-green-100 pt-1 pb-1" v-else> activo </Chip>
      </template>
    </Column>
    <Column field="updatedAt" header="Última actualización" sortable>
      <template #body="{ data, field }">
        <span>
          {{ formatDateWithHour(data[field]) }}
        </span>
      </template>
    </Column>
    <Column header="Acciones" style="width: 20%">
      <template #body="{ data }">
        <ConfirmPopup></ConfirmPopup>
        <div class="flex">
          <Button
            v-if="data.logo"
            class="action-btn p-button-sm mr-2"
            icon="pi pi-eye"
            @click="watchLogo(data.logo)"
            severity="primary"
            v-tooltip.bottom="'Ver logo'"
          />
          <Button
            class="action-btn p-button-sm mr-2"
            icon="pi pi-eye"
            v-tooltip.bottom="'Subir un logo'"
          >
            <input
              type="file"
              @change="saveFile($event, data['id'])"
              style="width: 100%"
              accept="image/*"
              class="opacity-0 pointer absolute"
            />
            <i class="pi pi-file-import"></i>
          </Button>
          <Button
            class="action-btn view p-button-sm bg-gray-300 border-gray-400 mr-2 shadow-1 text-primary"
            icon="pi pi-lock"
            @click="
              enableOrDisableCompany($event, data['id'], data['isInactive'])
            "
            severity="secondary"
            v-tooltip.bottom="'Cambiar estado'"
          />
          <Button
            class="action-btn view p-button-sm"
            icon="pi pi-trash"
            @click="deleteCompany($event, data['id'])"
            severity="danger"
            v-tooltip.bottom="'Eliminar'"
          />
        </div>
      </template>
    </Column>
  </DataTable>
  <Footer
    is-auditable
    :record-ids="user?.companies"
    label="Ver registros de auditoria"
  >
    <template #left>
      <AuditButton
        v-if="user?.companies && user.companies.length > 0"
        :record-ids="companiesIds"
        label="Ver registros de auditoria"
        :entities="['Company']"
      />
      <AuditButton
        v-else-if="user?.roles.name === 'superadmin'"
        root-id="SUPERADMIN"
        label="Ver registros de auditoria"
        :entities="['Company']"
      />
    </template>
  </Footer>
</template>
<script setup lang="ts">
  import Toolset from '@/shared/components/Toolset.vue'
  import Footer from '@/shared/components/Footer.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import useNotifications from '@/store/notification'
  import { useConfirm } from 'primevue/useconfirm'
  import { computed, onMounted, reactive, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import Company from '../models/Company'
  import {
    deleteCompanyById,
    enableCompanyById,
    uploadCompanyLogo,
  } from '../services/companyService'
  import { formatDateWithHour } from '@/shared/utils/fomartDateWithHours'
  import { storeToRefs } from 'pinia'
  import useCompanies from '@/shared/composables/useCompanies'
  import { useUserStore } from '@/store/user'
  import HeadTitle from '@/shared/components/HeadTitle.vue'

  const toolset = ref()
  const router = useRouter()
  const confirm = useConfirm()
  const notificationStore = useNotifications()
  const { user } = storeToRefs(useUserStore())
  const loading = ref(false)

  onMounted(async () => {
    await refreshData()
  })

  const companies = reactive({
    data: [] as Company[],
    totalItems: 0,
  })

  const pagination = reactive({
    currentPage: 0,
    lastPage: 0,
    limit: 10,
  })

  const toAdd = () => {
    router.push('/companies/add')
  }

  const refreshData = async () => {
    loading.value = true
    try {
      const { companies: Companies } = await useCompanies()
      companies.data = Companies.value
      companies.totalItems = Companies.value.length
    } finally {
      loading.value = false
    }
  }

  const searcher = async () => {
    loading.value = true
    try {
      const { companies: Companies } = await useCompanies(toolset.value.search)
      companies.data = Companies.value
      companies.totalItems = Companies.value.length
    } finally {
      loading.value = false
    }
  }

  const deleteCompany = (event: any, id: string) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Realmente quieres eliminar este registro?',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'aceptar',
      rejectLabel: 'cancelar',
      accept: async () => {
        const response = await deleteCompanyById(id)
        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `Compañia eliminada con exito.`,
          })
          refreshData()
        }
      },
      reject: () => {},
    })
  }

  const enableOrDisableCompany = (
    event: any,
    id: string,
    isInactive: boolean
  ) => {
    confirm.require({
      target: event.currentTarget,
      message: `¿Realmente quieres ${
        isInactive ? 'habilitar' : 'inhabilitar'
      } este registro?`,
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-info',
      acceptLabel: 'aceptar',
      rejectLabel: 'cancelar',
      accept: async () => {
        const response = await enableCompanyById(id)

        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `El estado ha cambiado con exito.`,
          })
          refreshData()
        }
      },
      reject: () => {},
    })
  }

  const saveFile = async (event: any, id: string) => {
    const file = event.target.files[0]
    const response = await uploadCompanyLogo(id, file)
    if (response === 'OK') {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Logo de la compañia actualizado con exito.`,
      })
      refreshData()
    }
  }

  const watchLogo = (logo: string) => {
    window.open(logo, '_blank')
  }

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${companies.totalItems}`
  })

  const companiesIds = computed(() => companies.data.map((c) => c.id))
</script>
